import React, { useState } from 'react';
import {
  Box, Chip, Collapse, IconButton, Link, List, ListItem, ListItemText, Stack, Tooltip, Typography,
} from '@mui/material';
import { ExpandMore as ExpandIcon, ExpandLess as CollapseIcon, OpenInNew as OpenIcon } from '@mui/icons-material';
import { verdictMeta, sortResults } from './verdict';

const fmtDetail = (v) => {
  if (v === null || v === undefined || v === '') return null;
  if (Array.isArray(v)) return v.length ? v.slice(0, 8).map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ') + (v.length > 8 ? ` (+${v.length - 8})` : '') : null;
  if (typeof v === 'object') return JSON.stringify(v);
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  return String(v);
};

/** One provider's result row with an expandable details section. */
export function IntelResultRow({ result }) {
  const [open, setOpen] = useState(false);
  const rateLimited = !!(result.details && result.details.rateLimited === true);
  const meta = rateLimited ? { label: 'Rate limited', color: 'warning' } : verdictMeta(result.verdict);
  const details = Object.entries(result.details || {}).map(([k, v]) => [k, fmtDetail(v)]).filter(([, v]) => v);
  return (
    <ListItem alignItems="flex-start" divider sx={{ px: 0 }} data-testid={`intel-${result.provider}`}>
      <ListItemText
        primary={(
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip size="small" color={meta.color} label={meta.label} />
            <Typography variant="subtitle2">{result.label}</Typography>
            {typeof result.score === 'number' && result.verdict !== 'error' && result.verdict !== 'unknown' && (
              <Typography variant="caption" color="text.secondary">score {result.score}</Typography>
            )}
            {result.link && (
              <Tooltip title={`Open in ${result.label}`}>
                <IconButton size="small" component={Link} href={result.link} target="_blank" rel="noopener noreferrer"><OpenIcon fontSize="inherit" /></IconButton>
              </Tooltip>
            )}
            {details.length > 0 && (
              <IconButton size="small" onClick={() => setOpen((o) => !o)} aria-label="toggle details">{open ? <CollapseIcon fontSize="inherit" /> : <ExpandIcon fontSize="inherit" />}</IconButton>
            )}
          </Stack>
        )}
        secondary={(
          <Box component="span" sx={{ display: 'block' }}>
            <Typography variant="body2" component="span" color={rateLimited ? 'warning.main' : result.verdict === 'error' ? 'error.main' : 'text.secondary'}>{result.summary}</Typography>
            {(result.tags || []).length > 0 && (
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap component="span">
                {result.tags.slice(0, 8).map((t) => <Chip key={t} size="small" variant="outlined" label={t} />)}
              </Stack>
            )}
            <Collapse in={open} unmountOnExit>
              <Box component="span" sx={{ display: 'block', mt: 1, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>
                {details.map(([k, v]) => `${k}: ${v}`).join('\n')}
              </Box>
            </Collapse>
          </Box>
        )}
        secondaryTypographyProps={{ component: 'div' }}
      />
    </ListItem>
  );
}

/** Aggregated verdict chip + provider rows for one indicator lookup ({ type, value, results, overall, cached }). */
export default function IntelResultList({ lookup, dense = false }) {
  if (!lookup) return null;
  const overall = lookup.overall || { verdict: 'unknown', score: 0 };
  const meta = verdictMeta(overall.verdict);
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: dense ? 0.5 : 1 }}>
        <Chip color={meta.color} label={`${meta.label}${overall.score ? ` · ${overall.score}` : ''}`} size={dense ? 'small' : 'medium'} />
        <Typography variant={dense ? 'body2' : 'subtitle1'} sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{lookup.value}</Typography>
        <Chip size="small" variant="outlined" label={lookup.type} />
        {lookup.cached && <Chip size="small" variant="outlined" label="cached" />}
        {lookup.skipped && <Chip size="small" variant="outlined" label={`skipped: ${lookup.skipped}`} />}
      </Stack>
      {overall.sources && overall.sources.length > 0 && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>Flagged by {overall.sources.join(', ')}</Typography>
      )}
      <List dense disablePadding>
        {sortResults(lookup.results).map((r) => <IntelResultRow key={r.provider} result={r} />)}
      </List>
      {(!lookup.results || !lookup.results.length) && !lookup.skipped && (
        <Typography variant="body2" color="text.secondary">No enabled provider supports this indicator type.</Typography>
      )}
    </Box>
  );
}
